import { createSlice, PayloadAction } from "@reduxjs/toolkit";

type TabView = "appointments-list" | "unsigned-visits" | "signed-visits" | null;

export type MenuItemValue =
  | "phone-note"
  | "non-visit-note"
  | "email-note"
  | "poc-lab"
  | "prescription-form"
  | "document-med-pt-is-on"
  | "lab-order"
  | "imaging-order"
  | "medication-history"
  | "reconcile-medications"
  | "lab-reports"
  | "imaging-reports"
  | "imaging-orders"
  | "add-referral";

interface VisitNoteState {
  selectedPatientUuid: string;
  selectedProviderUuid: string;
  selectedAppointment: string;
  selectedNoteType: string;
  isEditable: boolean;
  editPermission: boolean;
  occurrenceDate: string;
  refetchEntities: string[];
  selectedTabView: TabView;
  selectedMenuOptions: MenuItemValue[];
  note: string;
}

const initialState: VisitNoteState = {
  selectedPatientUuid: "",
  selectedProviderUuid: "",
  selectedAppointment: "",
  selectedNoteType: "soap",
  isEditable: true,
  editPermission: true,
  occurrenceDate: "",
  refetchEntities: [],
  selectedTabView: null,
  selectedMenuOptions: [],
  note: "",
};

const visitNoteSlice = createSlice({
  name: "visitNote",
  initialState,
  reducers: {
    setSelectedPatientUuid: (state, action: PayloadAction<string>) => {
      state.selectedPatientUuid = action.payload;
    },
    setSelectedProvider: (state, action: PayloadAction<string>) => {
      state.selectedProviderUuid = action.payload;
    },
    setSelectedAppointment: (state, action: PayloadAction<string>) => {
      state.selectedAppointment = action.payload;
    },
    setSelectedNoteType: (state, action: PayloadAction<string>) => {
      state.selectedNoteType = action.payload;
    },
    setIsEditable: (state, action: PayloadAction<boolean>) => {
      state.isEditable = action.payload;
    },
    setEditPermission: (state, action: PayloadAction<boolean>) => {
      state.editPermission = action.payload;
    },
    setOccurenceDate: (state, action: PayloadAction<string>) => {
      state.occurrenceDate = action.payload;
    },
    addRefetchEntity: (state, action: PayloadAction<string>) => {
      if (!state.refetchEntities.includes(action.payload)) {
        state.refetchEntities.push(action.payload);
      }
    },
    removeRefetchEntity: (state, action: PayloadAction<string>) => {
      state.refetchEntities = state.refetchEntities.filter(
        (e) => e !== action.payload,
      );
    },
    setSelectedTabView: (state, action: PayloadAction<TabView>) => {
      state.selectedTabView = action.payload;
    },
    addSelectedMenuOption: (state, action: PayloadAction<MenuItemValue>) => {
      if (
        action.payload &&
        !state?.selectedMenuOptions?.includes(action.payload)
      ) {
        state?.selectedMenuOptions?.push(action.payload);
      }
    },
    removeSelectedMenuOption: (state, action: PayloadAction<MenuItemValue>) => {
      state.selectedMenuOptions = state.selectedMenuOptions.filter(
        (e) => e !== action.payload,
      );
    },
    clearSelectedMenuOptions: (state) => {
      state.selectedMenuOptions = [];
    },
    setNote: (state, action: PayloadAction<string>) => {
      state.note = action.payload;
    },
  },
});

export const {
  setSelectedPatientUuid,
  setSelectedProvider,
  setSelectedAppointment,
  setSelectedNoteType,
  setIsEditable,
  setEditPermission,
  setOccurenceDate,
  addRefetchEntity,
  removeRefetchEntity,
  setSelectedTabView,
  addSelectedMenuOption,
  removeSelectedMenuOption,
  clearSelectedMenuOptions,
  setNote,
} = visitNoteSlice.actions;

export default visitNoteSlice.reducer;
